import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon } from 'lucide-react';
import { DataTag } from './DataTag';
import { DistributionBars } from './DistributionBars';
import { MIN_GROUP_SIZE, compareDimensions } from '../../data/results';
import type { MainOption } from '../../types/pulse';
interface CompareViewProps {
  options: MainOption[];
  accent: string;
}
const SHADES = ['FF', 'B3', '73', '40'];
const ease = [0.22, 1, 0.36, 1] as const;
export function CompareView({
  options,
  accent
}: CompareViewProps) {
  const [dimensionId, setDimensionId] = useState<string>('all');
  const dimension = compareDimensions.find(d => d.id === dimensionId);
  return <div data-id="element-508" data-mp="35m0b7fouaw7z">
      <DataTag kind="pulse" detail="n = [N]" data-id="element-509" data-mp="35lqivlgbocz4" />

      <div className="-mx-1 mt-4 flex gap-1.5 overflow-x-auto px-1 pb-1 no-scrollbar" role="tablist" aria-label="Compare responses" data-id="element-510" data-mp="35kfhfsacnhb6">
        {[{
        id: 'all',
        label: 'All'
      }, ...compareDimensions].map(d => <button key={d.id} role="tab" aria-selected={dimensionId === d.id} onClick={() => setDimensionId(d.id)} className={`shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris ${dimensionId === d.id ? 'border-navy-900 bg-navy-900 text-white' : 'border-navy-900/10 bg-white text-navy-700 hover:border-navy-900/25'}`} data-id="element-511" data-mp="1z9sxma8didg">
            {d.id === 'all' ? 'All' : `Compare ${d.label.toLowerCase()}`}
          </button>)}
      </div>

      <div className="mt-5" data-id="element-512" data-mp="57uz31lwttd0">
        {!dimension ? <DistributionBars options={options} accent={accent} delay={0.1} data-id="element-513" data-mp="aclgxwy6pyo" /> : <div data-id="element-514" data-mp="270j51ipxuuwh">
            <ul className="space-y-4" data-id="element-515" data-mp="2700z1udh52bd">
              {dimension.groups.map((g, gi) => <li key={g.id} data-id="element-516" data-mp="221pofhfcuns3" data-mpi={gi}>
                  <div className="flex items-baseline justify-between text-[12px]" data-id="element-517" data-mp="222hl6vkourjy">
                    <span className={g.sufficient ? 'font-medium text-navy-900' : 'text-slate-400'} data-id="element-518" data-mp="5z34xvo9sa5t">{g.label}</span>
                    <span className="tabular-nums text-slate-400" data-id="element-519" data-mp="ayfskimytl9g">n = [n]</span>
                  </div>
                  {g.sufficient ? <div className="mt-1.5 flex h-3 gap-0.5 overflow-hidden rounded-full" data-id="element-520" data-mp="f1cdifzz9tgk">
                      {options.map((o, i) => {
                const value = Math.max(1, o.peerPercent + (g.deltas[i] ?? 0));
                return <motion.div key={o.id} className="h-full" style={{
                  backgroundColor: `${accent}${SHADES[i]}`
                }} initial={{
                  width: 0
                }} animate={{
                  width: `${value}%`
                }} transition={{
                  duration: 0.8,
                  delay: gi * 0.1 + i * 0.05,
                  ease
                }} title={`${o.short}: ${value}%`} data-id="element-521" data-mp="tuz2vb9i9f7l" data-mpi={i} />;
              })}
                    </div> : <p className="mt-1.5 flex items-center gap-1.5 rounded-full bg-mist-100 px-3 py-1 text-[11px] text-slate-400" data-id="element-522" data-mp="1hqu2wesmp3k1">
                      <LockIcon className="h-3 w-3" data-id="element-523" data-mp="1culh4ijxec3w" />
                      Not enough responses to compare (minimum {MIN_GROUP_SIZE})
                    </p>}
                </li>)}
            </ul>
            <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5" data-id="element-524" data-mp="20id54jetwdl0">
              {options.map((o, i) => <li key={o.id} className="flex items-center gap-1.5 text-[11px] text-slate-500" data-id="element-525" data-mp="1vquzvj5rvqu8" data-mpi={i}>
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{
              backgroundColor: `${accent}${SHADES[i]}`
            }} data-id="element-526" data-mp="1rqb70rs8f9dw" />
                  <span className="truncate" data-id="element-527" data-mp="2o8phq7l0qyhx">{o.short}</span>
                </li>)}
            </ul>
          </div>}
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-slate-400" data-id="element-528" data-mp="2jf4i6b3s1wgm">
        Descriptive data from a voluntary sample. Differences between groups do not imply causality.
      </p>
    </div>;
}